/** Connect Four — 7 columns × 6 rows (row 0 = top, row 5 = bottom). */

export const COLS = 7;
export const ROWS = 6;
export const EMPTY = 0;
export const P1 = 1;
export const P2 = 2;

export const GAME_MODE = {
  MULTIPLAYER: "multiplayer",
  CPU: "cpu",
};

export const TURN_LIMIT_SEC = 20;
export const TURN_LIMIT_MS = TURN_LIMIT_SEC * 1000;

export function freshTurnClock() {
  const now = Date.now();
  return { turnStartedAtMs: now, turnDeadlineMs: now + TURN_LIMIT_MS };
}

/** Pass turn when the active player's clock has expired. */
export function applyTurnTimeout(state) {
  if (String(state?.status) !== "playing") return null;
  const deadline = Number(state?.turnDeadlineMs);
  if (!deadline || Date.now() < deadline) return null;
  const current = Number(state.currentPlayer);
  if (current !== P1 && current !== P2) return null;
  return {
    currentPlayer: opponentPlayer(current),
    ...freshTurnClock(),
    updatedAtMs: Date.now(),
    version: Number(state?.version || 0) + 1,
  };
}

export function createEmptyBoard() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));
}

export function boardToFlat(board) {
  const flat = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      flat.push(Number(board[r][c]) || EMPTY);
    }
  }
  return flat;
}

export function boardFromFlat(flat) {
  const board = createEmptyBoard();
  if (!Array.isArray(flat)) return board;
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const v = Number(flat[r * COLS + c]);
      board[r][c] = v === P1 || v === P2 ? v : EMPTY;
    }
  }
  return board;
}

export function cloneBoard(board) {
  return board.map((row) => [...row]);
}

export function getDropRow(board, col) {
  if (col < 0 || col >= COLS) return -1;
  for (let r = ROWS - 1; r >= 0; r--) {
    if (board[r][col] === EMPTY) return r;
  }
  return -1;
}

export function isColumnFull(board, col) {
  return getDropRow(board, col) < 0;
}

/** @returns {{ board: number[][], row: number, col: number } | null} */
export function dropDisc(board, col, player) {
  const row = getDropRow(board, col);
  if (row < 0 || (player !== P1 && player !== P2)) return null;
  const next = cloneBoard(board);
  next[row][col] = player;
  return { board: next, row, col };
}

function countLine(board, r, c, dr, dc, player) {
  const cells = [[r, c]];
  for (const dir of [-1, 1]) {
    let nr = r + dr * dir;
    let nc = c + dc * dir;
    while (
      nr >= 0 &&
      nr < ROWS &&
      nc >= 0 &&
      nc < COLS &&
      board[nr][nc] === player
    ) {
      cells.push([nr, nc]);
      nr += dr * dir;
      nc += dc * dir;
    }
  }
  return cells;
}

/** @returns {number[][] | null} four [row,col] cells or null */
/** Firestore-safe encoding (no nested arrays). */
export function encodeWinningCells(cells) {
  if (!Array.isArray(cells)) return [];
  return cells
    .map((cell) => {
      if (Array.isArray(cell) && cell.length >= 2) {
        return `${Number(cell[0])},${Number(cell[1])}`;
      }
      const s = String(cell ?? "").trim();
      return /^\d+,\d+$/.test(s) ? s : "";
    })
    .filter(Boolean);
}

/** Decode encoded cells or legacy nested arrays. */
export function decodeWinningCells(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((cell) => {
      if (Array.isArray(cell) && cell.length >= 2) {
        return [Number(cell[0]), Number(cell[1])];
      }
      const parts = String(cell ?? "")
        .split(",")
        .map((n) => Number(n.trim()));
      if (parts.length >= 2 && parts.every((n) => Number.isFinite(n))) {
        return [parts[0], parts[1]];
      }
      return null;
    })
    .filter(Boolean);
}

export function findWinningCells(board, player) {
  if (player !== P1 && player !== P2) return null;
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (board[r][c] !== player) continue;
      const dirs = [
        [0, 1],
        [1, 0],
        [1, 1],
        [1, -1],
      ];
      for (const [dr, dc] of dirs) {
        const line = countLine(board, r, c, dr, dc, player);
        if (line.length >= 4) return line.slice(0, 4);
      }
    }
  }
  return null;
}

export function isBoardFull(board) {
  for (let c = 0; c < COLS; c++) {
    if (!isColumnFull(board, c)) return false;
  }
  return true;
}

export function playerFromUserId(state, userId) {
  const id = String(userId ?? "").trim();
  if (!id || !state) return EMPTY;
  if (id === String(state.player1UserId ?? "").trim()) return P1;
  if (id === String(state.player2UserId ?? "").trim()) return P2;
  return EMPTY;
}

/** Resolve P1/P2 from state ids, with join-order fallback from live lobby users. */
export function resolvePlayerForUser(state, userId, livePlayers = []) {
  const fromState = playerFromUserId(state, userId);
  if (fromState) return fromState;

  const id = String(userId ?? "").trim();
  if (!id) return EMPTY;

  const sorted = [...livePlayers].sort((a, b) => {
    const ta = Number(a?.timestamp ?? 0);
    const tb = Number(b?.timestamp ?? 0);
    if (ta !== tb) return ta - tb;
    return String(a?.userId ?? a?.id ?? "").localeCompare(
      String(b?.userId ?? b?.id ?? "")
    );
  });

  const idx = sorted.findIndex((p) => {
    const pid = String(p?.userId ?? p?.id ?? "").trim();
    return pid === id;
  });
  if (idx === 0) return P1;
  if (idx === 1) return P2;
  return EMPTY;
}

export function opponentPlayer(player) {
  return player === P1 ? P2 : player === P2 ? P1 : EMPTY;
}

export function createInitialConnect4State({
  player1UserId,
  player2UserId,
  mode = GAME_MODE.MULTIPLAYER,
  cpuPlayer = P2,
}) {
  return {
    cells: boardToFlat(createEmptyBoard()),
    cols: COLS,
    rows: ROWS,
    mode,
    cpuPlayer,
    player1UserId: String(player1UserId ?? ""),
    player2UserId: String(player2UserId ?? ""),
    currentPlayer: P1,
    status: "playing",
    winner: EMPTY,
    winningCells: [],
    moveCount: 0,
    lastMove: null,
    player1Wins: 0,
    player2Wins: 0,
    updatedAtMs: Date.now(),
    version: 1,
    ...freshTurnClock(),
  };
}

/** Apply a move to state object; returns new state fields or null if invalid. */
export function applyMove(state, col, actingPlayer) {
  const board = boardFromFlat(state?.cells);
  if (String(state?.status) !== "playing") return null;
  if (actingPlayer !== P1 && actingPlayer !== P2) return null;
  if (Number(state?.currentPlayer) !== actingPlayer) return null;

  const result = dropDisc(board, col, actingPlayer);
  if (!result) return null;

  const { board: nextBoard, row, col: placedCol } = result;
  const winCells = findWinningCells(nextBoard, actingPlayer);
  const full = isBoardFull(nextBoard);
  const moveCount = Number(state?.moveCount || 0) + 1;

  let status = "playing";
  let winner = EMPTY;
  let winningCells = [];
  let currentPlayer = opponentPlayer(actingPlayer);
  let player1Wins = Number(state?.player1Wins || 0);
  let player2Wins = Number(state?.player2Wins || 0);

  if (winCells) {
    status = "won";
    winner = actingPlayer;
    winningCells = encodeWinningCells(winCells);
    currentPlayer = actingPlayer;
    if (actingPlayer === P1) player1Wins += 1;
    else player2Wins += 1;
  } else if (full) {
    status = "draw";
    winner = EMPTY;
    currentPlayer = EMPTY;
  }

  return {
    cells: boardToFlat(nextBoard),
    currentPlayer,
    status,
    winner,
    winningCells,
    moveCount,
    lastMove: { row, col: placedCol, player: actingPlayer },
    player1Wins,
    player2Wins,
    updatedAtMs: Date.now(),
    version: Number(state?.version || 0) + 1,
    ...(status === "playing" ? freshTurnClock() : { turnDeadlineMs: 0, turnStartedAtMs: 0 }),
  };
}

export function parseConnect4State(data) {
  if (!data || typeof data !== "object") return null;
  return {
    ...data,
    cells: Array.isArray(data.cells) ? data.cells : boardToFlat(createEmptyBoard()),
    cols: COLS,
    rows: ROWS,
    mode: data.mode === GAME_MODE.CPU ? GAME_MODE.CPU : GAME_MODE.MULTIPLAYER,
    cpuPlayer: data.cpuPlayer === P1 ? P1 : P2,
    currentPlayer:
      Number(data.currentPlayer) === P2 ? P2 : Number(data.currentPlayer) === P1 ? P1 : P1,
    status: ["playing", "won", "draw"].includes(data.status) ? data.status : "playing",
    winner:
      Number(data.winner) === P2 ? P2 : Number(data.winner) === P1 ? P1 : EMPTY,
    winningCells: decodeWinningCells(data.winningCells),
    moveCount: Number(data.moveCount) || 0,
    version: Number(data.version) || 0,
    turnStartedAtMs: Number(data.turnStartedAtMs) || 0,
    turnDeadlineMs: Number(data.turnDeadlineMs) || 0,
  };
}
